import { ReactComponent as PlivoIcon } from '@shared/assets/icons/ic-plivo.svg'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { Flag } from 'primitives/icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../primitives/dropdown-menu'
import { Separator } from '../../../primitives/separator'
import {
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../../../primitives/sidebar'

interface SidebarHeaderDropdownProps {
  currentOrgUuid: string
  organizations: Array<{ id: string; name: string; region: string }>
  onOrganizationChange: (orgId: string) => void
  onCreateOrganization?: () => void
  onLogoClick?: () => void
  isCollapsed: boolean
}

const getRegionLabel = (region: string) => {
  return region === 'ap-south-1' ? 'India' : 'US'
}

const getRegionISO = (region: string) => {
  return region === 'ap-south-1' ? 'IN' : 'US'
}
export const AppSidebarHeaderDropdown: React.FC<SidebarHeaderDropdownProps> = ({
  currentOrgUuid,
  organizations,
  onOrganizationChange,
  onCreateOrganization,
  onLogoClick,
  isCollapsed,
}) => {
  const sortedOrganizations = [...(organizations || [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  )
  const loggedInOrg = organizations?.find((org) => org.id === currentOrgUuid)

  return (
    <SidebarHeader className="gap-4 pt-[22px]">
      <button
        type="button"
        onClick={onLogoClick}
        tabIndex={-1}
        className="cursor-pointer bg-transparent border-none p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="sidebar-logo-btn"
      >
        {isCollapsed ? (
          <PlivoIcon className="size-6 mx-auto dark:invert" />
        ) : (
          <span className="group/logo relative block w-fit mr-auto left-3">
            <img src="/plivo-logo.svg" className="h-[17px] dark:invert" alt="logo" />
            {/* glint sweep on hover, masked to the logo glyphs; band profile matches TirangaIcon's shimmer */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 group-hover/logo:animate-glint bg-glint dark:bg-glint-dark bg-[length:250%_100%] [mask-image:url('/plivo-logo.svg')] [mask-size:100%_100%]"
            />
          </span>
        )}
      </button>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-hv1 data-[state=open]:text-sidebar-accent-foreground hover:bg-hv1 cursor-pointer bg-white dark:bg-hv1 border border-sidebar-border px-2.5 py-1.5 h-auto"
                data-testid="organization-menu-trigger"
              >
                <div className="flex size-6 shrink-0 items-center justify-center rounded-full">
                  <Flag iso={getRegionISO(loggedInOrg?.region || 'US')} circle size={20} />
                </div>
                {!isCollapsed && (
                  <>
                    <div className="grid flex-1 text-left text-s-500 leading-tight">
                      <span className="truncate">
                        {loggedInOrg ? (
                          loggedInOrg.name
                        ) : (
                          <span className="inline-block h-4 w-24 animate-pulse rounded bg-muted" />
                        )}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4 text-tertiary" />
                  </>
                )}
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-48 rounded-lg gap-1 flex flex-col hoverbar-holder"
              align="start"
              side="bottom"
              sideOffset={4}
            >
              {sortedOrganizations.map((org) => {
                const isActiveOrg = org.id === currentOrgUuid

                return (
                  <DropdownMenuItem
                    key={org.id}
                    data-testid={`organization-menuitem-${org.id}`}
                    className={isActiveOrg ? 'pointer-events-none bg-hv1' : ''}
                    onClick={() => onOrganizationChange(org.id)}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <div className="flex size-6 shrink-0 items-center justify-center rounded-full">
                        <Flag iso={getRegionISO(org.region)} circle size={20} />
                      </div>
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-p-600 truncate">{org.name}</span>
                        <span className="text-xs-400 text-tertiary">
                          {getRegionLabel(org.region)}
                        </span>
                      </div>
                      {isActiveOrg && <Check className="size-4 shrink-0" strokeWidth={1.5} />}
                    </div>
                  </DropdownMenuItem>
                )
              })}
              {onCreateOrganization && (
                <>
                  {sortedOrganizations.length > 0 && <Separator />}
                  <DropdownMenuItem
                    onClick={onCreateOrganization}
                    className="gap-1 flex items-center cursor-pointer text-s-400"
                    data-testid="create-organization-button"
                  >
                    <Plus className="size-4" strokeWidth={1.5} />
                    Create New Organization
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
  )
}
